import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Search, Filter, Eye, Download, Users, Check, X, Trash2, Mail, LogIn, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_OPTIONS = ['All', 'Pending', 'Approved', 'Active', 'Inactive', 'Rejected'];

export default function VolunteerDatabase() {
  const [volunteers, setVolunteers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedVolunteer, setSelectedVolunteer] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    fetchVolunteers();
  }, [statusFilter, searchTerm]);

  const fetchVolunteers = async () => {
    try {
      let url = `${API}/volunteers`;
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'All') {
        params.append('status', statusFilter);
      }
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setVolunteers(data);
      }
    } catch (error) {
      console.error('Error fetching volunteers:', error);
      toast.error('Failed to load volunteers');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (volunteer) => {
    try {
      const response = await fetch(`${API}/volunteers/${volunteer.volunteer_id}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: 'Approved' })
      });
      
      if (!response.ok) throw new Error('Failed to approve');
      
      toast.success(`${volunteer.first_name} ${volunteer.last_name} approved! Email notification sent.`);
      fetchVolunteers();
    } catch (error) {
      toast.error('Failed to approve volunteer');
    }
  };

  const handleReject = async () => {
    if (!selectedVolunteer) return;
    
    try {
      const response = await fetch(`${API}/volunteers/${selectedVolunteer.volunteer_id}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          status: 'Rejected',
          rejection_reason: rejectionReason 
        })
      });
      
      if (!response.ok) throw new Error('Failed to reject');
      
      toast.success(`${selectedVolunteer.first_name} ${selectedVolunteer.last_name} rejected. Email notification sent.`);
      setShowRejectDialog(false);
      setSelectedVolunteer(null);
      setRejectionReason('');
      fetchVolunteers();
    } catch (error) {
      toast.error('Failed to reject volunteer');
    }
  };

  const openRejectDialog = (volunteer) => {
    setSelectedVolunteer(volunteer);
    setRejectionReason('');
    setShowRejectDialog(true);
  };

  const openDeleteDialog = (volunteer) => {
    setSelectedVolunteer(volunteer);
    setShowDeleteDialog(true);
  };

  const handleDelete = async () => {
    if (!selectedVolunteer) return;
    
    try {
      const response = await fetch(`${API}/volunteers/${selectedVolunteer.volunteer_id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (!response.ok) throw new Error('Failed to delete');
      
      toast.success(`${selectedVolunteer.first_name} ${selectedVolunteer.last_name} deleted successfully`);
      setShowDeleteDialog(false);
      setSelectedVolunteer(null);
      fetchVolunteers();
    } catch (error) {
      toast.error('Failed to delete volunteer');
    }
  };

  const exportToCSV = async () => {
    try {
      const response = await fetch(`${API}/volunteers/export/csv`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `volunteers_export_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Volunteers exported successfully!');
      } else {
        toast.error('Failed to export volunteers');
      }
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Failed to export volunteers');
    }
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      'Pending': 'status-pending',
      'Approved': 'status-approved',
      'Active': 'status-active',
      'Inactive': 'status-inactive',
      'Rejected': 'status-rejected'
    };
    return <span className={`status-badge ${statusClasses[status] || 'status-pending'}`}>{status}</span>;
  };

  const getBackgroundCheckBadge = (status) => {
    const statusClasses = {
      'Pending': 'bg-amber-100 text-amber-800',
      'Completed': 'bg-emerald-100 text-emerald-800',
      'Expired': 'bg-red-100 text-red-800'
    };
    return (
      <Badge variant="secondary" className={statusClasses[status] || 'bg-slate-100 text-slate-600'}>
        {status}
      </Badge>
    );
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="volunteer-database">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-[#1a3a5c]" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Volunteer Database
            </h1>
            <p className="text-slate-500 mt-1">{volunteers.length} volunteers total</p>
          </div>
          <Button variant="outline" className="border-2" onClick={exportToCSV} data-testid="export-btn">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>

        {/* Filters */}
        <Card className="border-0 shadow-md">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <Input
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-48" data-testid="status-filter">
                  <Filter className="w-4 h-4 mr-2 text-slate-400" />
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((status) => (
                    <SelectItem key={status} value={status}>{status}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="border-0 shadow-md">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-4 border-[#5cc8e8] border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : volunteers.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-semibold">Name</TableHead>
                      <TableHead className="font-semibold">Email</TableHead>
                      <TableHead className="font-semibold">Status</TableHead>
                      <TableHead className="font-semibold">Background Check</TableHead>
                      <TableHead className="font-semibold">Hours</TableHead>
                      <TableHead className="font-semibold">Emails Sent</TableHead>
                      <TableHead className="font-semibold">Portal Access</TableHead>
                      <TableHead className="font-semibold">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {volunteers.map((volunteer) => (
                      <TableRow key={volunteer.volunteer_id} className="table-row-hover">
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-[#5cc8e8]/10 rounded-full flex items-center justify-center">
                              <span className="text-xs font-medium text-[#1a3a5c]">
                                {volunteer.first_name?.[0]}{volunteer.last_name?.[0]}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium text-[#1a3a5c]">
                                {volunteer.first_name} {volunteer.last_name}
                              </p>
                              <p className="text-xs text-slate-500">{volunteer.city}, {volunteer.state}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-slate-600">{volunteer.email}</TableCell>
                        <TableCell>{getStatusBadge(volunteer.status)}</TableCell>
                        <TableCell>{getBackgroundCheckBadge(volunteer.background_check_status)}</TableCell>
                        <TableCell className="font-medium text-[#1a3a5c]">{volunteer.total_hours || 0}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Mail className="w-4 h-4 text-slate-400" />
                            <span className="text-sm text-slate-600">
                              {volunteer.emails_sent?.length || 0}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {volunteer.portal_accessed ? (
                            <div className="flex items-center gap-1">
                              <LogIn className="w-4 h-4 text-green-500" />
                              <span className="text-xs text-green-600">Yes</span>
                            </div>
                          ) : (
                            <span className="text-xs text-slate-400">No</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            {volunteer.status === 'Pending' && (
                              <>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                  onClick={() => handleApprove(volunteer)}
                                  data-testid={`approve-${volunteer.volunteer_id}`}
                                >
                                  <Check className="w-4 h-4" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => openRejectDialog(volunteer)}
                                  data-testid={`reject-${volunteer.volunteer_id}`}
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                            <Link to={`/admin/volunteers/${volunteer.volunteer_id}`}>
                              <Button variant="ghost" size="sm" data-testid={`view-volunteer-${volunteer.volunteer_id}`}>
                                <Eye className="w-4 h-4 mr-1" />
                                View
                              </Button>
                            </Link>
                            {volunteer.status === 'Approved' && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                onClick={() => window.open(`/volunteer-portal?email=${encodeURIComponent(volunteer.email)}&auto=true`, '_blank')}
                                title="View Volunteer Portal"
                              >
                                <ExternalLink className="w-4 h-4" />
                              </Button>
                            )}
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => openDeleteDialog(volunteer)}
                              data-testid={`delete-volunteer-${volunteer.volunteer_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-64 text-slate-500">
                <Users className="w-12 h-12 mb-4 text-slate-300" />
                <p>No volunteers found</p>
                <p className="text-sm">Try adjusting your search or filters</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Reject Dialog */}
        <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reject Volunteer Application</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-slate-600">
                Are you sure you want to reject the application from{' '}
                <strong>{selectedVolunteer?.first_name} {selectedVolunteer?.last_name}</strong>?
              </p>
              <div className="space-y-2">
                <Label htmlFor="rejection-reason">Reason (optional)</Label>
                <Textarea
                  id="rejection-reason"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Provide a reason for rejection (will be included in the notification email)"
                  rows={3}
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button 
                  variant="outline" 
                  onClick={() => setShowRejectDialog(false)} 
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleReject}
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  data-testid="confirm-reject-btn"
                >
                  Reject Application
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Volunteer</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-slate-600">
                Are you sure you want to permanently delete{' '}
                <strong>{selectedVolunteer?.first_name} {selectedVolunteer?.last_name}</strong>?
              </p>
              <p className="text-sm text-red-600">
                This action cannot be undone. All data associated with this volunteer will be removed.
              </p>
              <div className="flex gap-2 pt-2">
                <Button 
                  variant="outline" 
                  onClick={() => setShowDeleteDialog(false)} 
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleDelete}
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  data-testid="confirm-delete-btn"
                >
                  Delete Volunteer
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
